import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import {
  Entity,
  RELATION_OWNED_BY,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export type CatalogScope = 'owned' | 'starred' | 'all';
export type SortField = 'name' | 'system' | 'owner' | 'type' | 'lifecycle';
export type SortOrder = 'asc' | 'desc';
export type CatalogView = 'list' | 'grid';

export type CatalogQueryState = {
  scope: CatalogScope;
  scopeExplicit: boolean;
  kind: string;
  types: string[];
  owners: string[];
  lifecycles: string[];
  tags: string[];
  text: string;
  sortField: SortField;
  sortOrder: SortOrder;
  offset: number;
  limit: number;
  view: CatalogView;
};

export type FacetOption = { value: string; count: number };

type FilterValue = string | symbol | (string | symbol)[];
export type CatalogFilter = Record<string, FilterValue>;

export type CatalogVariantOptions = {
  /** Empty string means "any kind". */
  defaultKind: string;
  fixedKind?: boolean;
  defaultScope: CatalogScope;
  baseFilter?: CatalogFilter;
  clientPredicate?: (entity: Entity) => boolean;
};

export const CATALOG_OPTIONS: CatalogVariantOptions = {
  defaultKind: 'component',
  defaultScope: 'owned',
};

const DEFAULT_LIMIT = 25;
export const PAGE_SIZES = [10, 25, 50, 100];

const SORT_FIELDS: Record<SortField, string> = {
  name: 'metadata.name',
  system: 'spec.system',
  owner: 'spec.owner',
  type: 'spec.type',
  lifecycle: 'spec.lifecycle',
};

const TEXT_FIELDS = [
  'metadata.name',
  'metadata.title',
  'metadata.description',
  'spec.type',
  'spec.system',
  'spec.owner',
];

function normalizeOwnerRef(value: string): string {
  try {
    return stringifyEntityRef(
      parseEntityRef(value, { defaultKind: 'group', defaultNamespace: 'default' }),
    ).toLocaleLowerCase('en-US');
  } catch {
    return value.toLocaleLowerCase('en-US');
  }
}

function readList(params: URLSearchParams, key: string): string[] {
  return params
    .getAll(`filters[${key}]`)
    .flatMap(v => v.split(','))
    .map(v => v.trim())
    .filter(Boolean);
}

function parseKind(params: URLSearchParams, opts: CatalogVariantOptions): string {
  if (opts.fixedKind) return opts.defaultKind;
  const raw = params.get('filters[kind]');
  if (raw === null) return opts.defaultKind;
  return raw === 'all' ? '' : raw.toLocaleLowerCase('en-US');
}

function parseState(
  params: URLSearchParams,
  opts: CatalogVariantOptions,
): CatalogQueryState {
  const user = params.get('filters[user]');
  const scope: CatalogScope =
    user === 'all' || user === 'starred' || user === 'owned'
      ? user
      : opts.defaultScope;
  const [sortFieldRaw, sortOrderRaw] = (params.get('sort') ?? 'name:asc').split(':');
  const sortField = (Object.keys(SORT_FIELDS) as SortField[]).includes(
    sortFieldRaw as SortField,
  )
    ? (sortFieldRaw as SortField)
    : 'name';
  const limit = Number.parseInt(params.get('limit') ?? '', 10);
  const offset = Number.parseInt(params.get('offset') ?? '', 10);
  return {
    scope,
    scopeExplicit: Boolean(user),
    kind: parseKind(params, opts),
    types: readList(params, 'type').map(v => v.toLocaleLowerCase('en-US')),
    owners: readList(params, 'owners').map(normalizeOwnerRef),
    lifecycles: readList(params, 'lifecycles').map(v => v.toLocaleLowerCase('en-US')),
    tags: readList(params, 'tags'),
    text: params.get('filters[text]') ?? '',
    sortField,
    sortOrder: sortOrderRaw === 'desc' ? 'desc' : 'asc',
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
    limit: PAGE_SIZES.includes(limit) ? limit : DEFAULT_LIMIT,
    view: params.get('view') === 'grid' ? 'grid' : 'list',
  };
}

function serializeState(
  state: CatalogQueryState,
  opts: CatalogVariantOptions,
): URLSearchParams {
  const params = new URLSearchParams();
  if (!opts.fixedKind) params.set('filters[kind]', state.kind || 'all');
  params.set('filters[user]', state.scope);
  state.types.forEach(v => params.append('filters[type]', v));
  state.owners.forEach(v => params.append('filters[owners]', v));
  state.lifecycles.forEach(v => params.append('filters[lifecycles]', v));
  state.tags.forEach(v => params.append('filters[tags]', v));
  if (state.text) params.set('filters[text]', state.text);
  if (state.sortField !== 'name' || state.sortOrder !== 'asc') {
    params.set('sort', `${state.sortField}:${state.sortOrder}`);
  }
  if (state.offset) params.set('offset', String(state.offset));
  if (state.limit !== DEFAULT_LIMIT) params.set('limit', String(state.limit));
  if (state.view !== 'list') params.set('view', state.view);
  return params;
}

export function useCatalogUrlState(opts: CatalogVariantOptions) {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseState(searchParams, opts), [searchParams, opts]);

  const update = useCallback(
    (patch: Partial<CatalogQueryState>, keepOffset = false) => {
      const next = { ...parseState(searchParams, opts), ...patch };
      if (!keepOffset && !('offset' in patch)) next.offset = 0;
      setSearchParams(serializeState(next, opts), { replace: true });
    },
    [searchParams, setSearchParams, opts],
  );

  const reset = useCallback(() => {
    const current = parseState(searchParams, opts);
    setSearchParams(
      serializeState(
        {
          ...parseState(new URLSearchParams(), opts),
          kind: current.kind,
          view: current.view,
          limit: current.limit,
        },
        opts,
      ),
      { replace: true },
    );
  }, [searchParams, setSearchParams, opts]);

  return { state, update, reset };
}

function compareEntities(
  a: Entity,
  b: Entity,
  field: SortField,
  order: SortOrder,
): number {
  const pick = (e: Entity): string => {
    const spec = (e.spec ?? {}) as Record<string, unknown>;
    switch (field) {
      case 'name':
        return e.metadata.name;
      case 'owner':
        return String(spec.owner ?? '');
      default:
        return String(spec[field] ?? '');
    }
  };
  const result = pick(a).localeCompare(pick(b), 'es', { sensitivity: 'base' });
  return order === 'asc' ? result : -result;
}

function matchesClientFilters(entity: Entity, state: CatalogQueryState, ownerRefs?: string[]) {
  const spec = (entity.spec ?? {}) as Record<string, unknown>;
  const lower = (v: unknown) => String(v ?? '').toLocaleLowerCase('en-US');
  if (state.types.length && !state.types.includes(lower(spec.type))) return false;
  if (state.lifecycles.length && !state.lifecycles.includes(lower(spec.lifecycle))) {
    return false;
  }
  if (state.tags.length) {
    const tags = entity.metadata.tags ?? [];
    if (!state.tags.some(t => tags.includes(t))) return false;
  }
  if (ownerRefs) {
    const owners = (entity.relations ?? [])
      .filter(r => r.type === RELATION_OWNED_BY)
      .map(r => r.targetRef.toLocaleLowerCase('en-US'));
    if (!owners.some(o => ownerRefs.includes(o))) return false;
  }
  if (state.text) {
    const term = state.text.toLocaleLowerCase('en-US');
    const haystack = [
      entity.metadata.name,
      entity.metadata.title,
      entity.metadata.description,
      spec.type,
      spec.system,
      spec.owner,
    ]
      .map(lower)
      .join(' ');
    if (!haystack.includes(term)) return false;
  }
  return true;
}

export type CatalogQueryResult = {
  items: Entity[];
  totalItems: number;
  loading: boolean;
  error?: Error;
  fetchedAt?: Date;
  latencyMs?: number;
};

export function useCatalogQuery(
  state: CatalogQueryState,
  reloadKey: number,
  opts: CatalogVariantOptions,
) {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const { starredEntities } = useStarredEntities();

  const [ownershipRefs, setOwnershipRefs] = useState<string[] | undefined>();
  useEffect(() => {
    identityApi
      .getBackstageIdentity()
      .then(identity =>
        setOwnershipRefs(
          identity.ownershipEntityRefs.map(r => r.toLocaleLowerCase('en-US')),
        ),
      )
      .catch(() => setOwnershipRefs([]));
  }, [identityApi]);

  const starredRefs = useMemo(
    () =>
      [...starredEntities].filter(ref => {
        try {
          return (
            !state.kind ||
            parseEntityRef(ref).kind.toLocaleLowerCase('en-US') === state.kind
          );
        } catch {
          return false;
        }
      }),
    [starredEntities, state.kind],
  );

  const [kinds, setKinds] = useState<FacetOption[]>([]);
  useEffect(() => {
    catalogApi
      .getEntityFacets({ facets: ['kind'], filter: opts.baseFilter })
      .then(r => setKinds(r.facets.kind ?? []))
      .catch(() => setKinds([]));
  }, [catalogApi, reloadKey, opts]);

  const kindFilter = useMemo<CatalogFilter>(
    () => ({ ...(opts.baseFilter ?? {}), ...(state.kind ? { kind: state.kind } : {}) }),
    [opts, state.kind],
  );

  const [starredCount, setStarredCount] = useState<number | undefined>();
  useEffect(() => {
    if (!opts.clientPredicate || !starredRefs.length) {
      setStarredCount(starredRefs.length);
      return undefined;
    }
    let cancelled = false;
    catalogApi
      .getEntitiesByRefs({ entityRefs: starredRefs })
      .then(r => {
        if (!cancelled) {
          setStarredCount(
            r.items.filter((e): e is Entity => Boolean(e) && opts.clientPredicate!(e!))
              .length,
          );
        }
      })
      .catch(() => {
        if (!cancelled) setStarredCount(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, starredRefs, opts, reloadKey]);

  const [counts, setCounts] = useState<{ owned?: number; all?: number }>({});
  useEffect(() => {
    if (!ownershipRefs) return undefined;
    let cancelled = false;
    const count = (filter: CatalogFilter) =>
      catalogApi
        .queryEntities({ filter, limit: 1, fields: ['metadata.name'] })
        .then(r => r.totalItems)
        .catch(() => undefined);
    Promise.all([
      ownershipRefs.length
        ? count({ ...kindFilter, 'relations.ownedBy': ownershipRefs })
        : Promise.resolve(0),
      count(kindFilter),
    ]).then(([owned, all]) => {
      if (!cancelled) setCounts({ owned, all });
    });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, ownershipRefs, kindFilter, reloadKey]);

  const [facets, setFacets] = useState<Record<string, FacetOption[]>>({});
  useEffect(() => {
    if (!ownershipRefs) return undefined;
    let cancelled = false;
    const filter: CatalogFilter = { ...kindFilter };
    if (state.scope === 'owned' && ownershipRefs.length) {
      filter['relations.ownedBy'] = ownershipRefs;
    }
    catalogApi
      .getEntityFacets({
        filter,
        facets: ['spec.type', 'spec.lifecycle', 'metadata.tags', 'relations.ownedBy'],
      })
      .then(r => {
        if (!cancelled) setFacets(r.facets);
      })
      .catch(() => {
        if (!cancelled) setFacets({});
      });
    return () => {
      cancelled = true;
    };
  }, [catalogApi, ownershipRefs, kindFilter, state.scope, reloadKey]);

  const [result, setResult] = useState<CatalogQueryResult>({
    items: [],
    totalItems: 0,
    loading: true,
  });
  const requestId = useRef(0);

  useEffect(() => {
    if (!ownershipRefs) return;
    const id = ++requestId.current;
    const started = performance.now();
    setResult(prev => ({ ...prev, loading: true, error: undefined }));

    const finish = (items: Entity[], totalItems: number) => {
      if (id !== requestId.current) return;
      setResult({
        items,
        totalItems,
        loading: false,
        fetchedAt: new Date(),
        latencyMs: Math.round(performance.now() - started),
      });
    };
    const fail = (error: Error) => {
      if (id !== requestId.current) return;
      setResult(prev => ({ ...prev, loading: false, error }));
    };

    let ownerFilter: string[] | undefined = state.owners.length ? state.owners : undefined;
    if (state.scope === 'owned') {
      ownerFilter = ownerFilter
        ? ownerFilter.filter(o => ownershipRefs.includes(o))
        : ownershipRefs;
      if (!ownerFilter.length) {
        finish([], 0);
        return;
      }
    }

    if (state.scope === 'starred') {
      if (!starredRefs.length) {
        finish([], 0);
        return;
      }
      catalogApi
        .getEntitiesByRefs({ entityRefs: starredRefs })
        .then(response => {
          const all = response.items
            .filter((e): e is Entity => Boolean(e))
            .filter(e => !opts.clientPredicate || opts.clientPredicate(e))
            .filter(e => matchesClientFilters(e, state, ownerFilter))
            .sort((a, b) => compareEntities(a, b, state.sortField, state.sortOrder));
          finish(all.slice(state.offset, state.offset + state.limit), all.length);
        })
        .catch(fail);
      return;
    }

    const filter: CatalogFilter = { ...kindFilter };
    if (state.types.length) filter['spec.type'] = state.types;
    if (state.lifecycles.length) filter['spec.lifecycle'] = state.lifecycles;
    if (state.tags.length) filter['metadata.tags'] = state.tags;
    if (ownerFilter) filter['relations.ownedBy'] = ownerFilter;

    catalogApi
      .queryEntities({
        filter,
        limit: state.limit,
        offset: state.offset,
        orderFields: [
          { field: SORT_FIELDS[state.sortField], order: state.sortOrder },
          ...(state.sortField === 'name'
            ? []
            : [{ field: 'metadata.name', order: 'asc' as const }]),
        ],
        ...(state.text.trim()
          ? { fullTextFilter: { term: state.text.trim(), fields: TEXT_FIELDS } }
          : {}),
      })
      .then(response => finish(response.items, response.totalItems))
      .catch(fail);
  }, [catalogApi, ownershipRefs, starredRefs, state, kindFilter, opts, reloadKey]);

  return {
    result,
    kinds,
    facets,
    counts: { ...counts, starred: starredCount },
    ownershipReady: Boolean(ownershipRefs),
  };
}
